import { shallowMemoize, getDeratedValue, getPercentage, isModeDefault } from '../../util';
import { EnergyState } from 'store/slices/EnergySlice/types';
import { Design, HourlyEngeryTOFAndSolarAccessData, perPanelEnergyDetails, TOFAndSolarAccessPerSgement } from './types';
import {
	DEFAULT_KONVA_SCALE,
	DEFAULT_KONVA_STAGE_POSITION,
	IRRADIANCE_UPRATE_RATE,
	MAX_ZOOM_LIMIT_FOR_SAVE,
	PANEL_OFF_COLOR,
	DEFAULT_SHADING_VAL,
	STANDARD_IMAGERY_SIZE
} from '../../constants';
import {
	getBaseRasterImage, getExistingPanelsGroupID, getPanelDrawerGroup, konvaImageIds, KonvaGroupNames, setZoomLevelOnStageFromCenter,
} from 'components/tool/utils';
import Konva from 'konva';

export const getEnergyData = shallowMemoize(function returnProductionData(
	energy: EnergyState, roofSegsFromWhichUserHasSelectedPanels: string[],
	roofSegs: { [key: string]: RasterRoofSegment }, offset: number, panel: { wattage: number, key: string },
	panelsForMonthlyCalculation: { [key: string]: perPanelEnergyDetails } | undefined, designUUID: string, energyDerateRate: number,
	hourlyEngeryTOFAndSolarAccessData: HourlyEngeryTOFAndSolarAccessData | undefined) {

	const facets: {
		pitchDegrees: number;
		azimuthDegrees: number;
		panelsCount: number;
		yearlyEnergyDcKwh: number;
		monthlyEnergyDcKwh: number[];
		orientation: Orientation | 'MIXED';
		solarAccess: number;
		solarAccessMonthly: number[];
		irradiance: number;
		annualTOF: number;
		annualTSRF: number;
		shading: number;
		isGroundMount: boolean;
	}[] = [];

	const { selectedPanels, totalNoOfPanelsSelectedByUser, panelCapacityWatts, deratedEnergy, currentBillAmount, annualInputEnergy } = energy;
	let yearlyEnergy = 0, monthlyEnergy = 0, monthlyDataAvailable = true, designIrradiance = 0;
	let totalYearMontlyEnergy = 0, totalYearAdjustedMontlyEnergy = 0;
	const tOFAndSolarAccessData = reStructureTOFAndSolarAccessData(hourlyEngeryTOFAndSolarAccessData);

	roofSegsFromWhichUserHasSelectedPanels.forEach((roofId) => {
		const roofSeg = roofSegs[roofId] as RasterRoofSegment & { simplifiedHullCoords: number[][] };
		if (!roofSeg) return;
		if (!panelsForMonthlyCalculation || Object.keys(panelsForMonthlyCalculation).length === 0)
			monthlyDataAvailable = false;

		const maxPanelEnergyForEachMonth = new Array(12).fill(0);
		let yearlyEnergyDcKwh = 0, panelsCount = 0, selectedPanelsIrradiance = 0, totalMonthlyEnergyPerFacet=0;
		let monthlyEnergyDcKwh = new Array(12).fill(0);
		roofSeg.panels.forEach((panel) => {
			// Calculating maxPanelEnergyForEachMonth as we want overall max energy from all panels(selected as well as unselected)
			if (monthlyDataAvailable) {
				const { monthlyEnergy: monthlyEnergies = [] } = panelsForMonthlyCalculation?.[panel.id] || {};
				monthlyEnergies?.forEach((monthEnergy: number, index: number) => {
					maxPanelEnergyForEachMonth[index] = Math.max(+(maxPanelEnergyForEachMonth[index]), +(monthEnergy));
				});
			}
			if (selectedPanels.includes(panel.id)) {
				if (monthlyDataAvailable) {
					const { monthlyEnergy: monthlyEnergies = [] } = panelsForMonthlyCalculation?.[panel.id] || {};
					monthlyEnergies?.forEach((monthEnergy: number, index: number) => {
						const mDeratedEnergy = getDeratedValue(+monthEnergy, energyDerateRate);
						monthlyEnergyDcKwh[index] += mDeratedEnergy;
						monthlyEnergy += mDeratedEnergy;
						totalMonthlyEnergyPerFacet+=mDeratedEnergy;
					});
				}
				yearlyEnergyDcKwh += panel.yearlyEnergyDcKwh;
				panelsCount++;
				selectedPanelsIrradiance += panel.irradiance;
			}
		});

		const yearlyDeratedEnergyPerFacet = getDeratedValue(yearlyEnergyDcKwh, energyDerateRate);

		//Adjusrt monthly to match annual
		totalYearMontlyEnergy += totalMonthlyEnergyPerFacet;
		const { adjustedEnergy, adjustedTotal } = adjustEnergyToMatchAnnual(
			yearlyDeratedEnergyPerFacet, monthlyEnergyDcKwh, totalMonthlyEnergyPerFacet);
		monthlyEnergyDcKwh = adjustedEnergy;
		totalYearAdjustedMontlyEnergy += adjustedTotal;

		// Solar Access Calculations
		const tsrfTofAndSolarAccessDataPerFacet = getTsrfTofAndSolarAccessDataPerFacet(tOFAndSolarAccessData, selectedPanels, roofId);
		yearlyEnergy += yearlyEnergyDcKwh;
		designIrradiance += selectedPanelsIrradiance;

		facets.push({
			yearlyEnergyDcKwh: yearlyDeratedEnergyPerFacet,
			monthlyEnergyDcKwh: monthlyEnergyDcKwh,
			azimuthDegrees: Math.round(roofSeg.azimuthDegrees),
			pitchDegrees: roofSeg.pitchDegrees,
			orientation: roofSeg.orientation,
			panelsCount,
			irradiance: (selectedPanelsIrradiance + getPercentage(selectedPanelsIrradiance, IRRADIANCE_UPRATE_RATE)) / panelsCount,
			shading: roofSeg.shading || DEFAULT_SHADING_VAL, //discuss
			...tsrfTofAndSolarAccessDataPerFacet,
			isGroundMount: !!roofSeg?.isGroundMount
		});
	});

	console.log('facets', facets, 'TOTAL_MONTHLY_ENERGY =',totalYearMontlyEnergy);
	const hourlyEnergy = getHouseHourlyEngery(hourlyEngeryTOFAndSolarAccessData, selectedPanels, energyDerateRate, deratedEnergy);

	const design: Omit<Design, 'isSunroofAvailable' | 'isApprovalRequired' | 'images'> = {
		yearlyEnergy: getDeratedValue(yearlyEnergy, energyDerateRate),
		monthlyEnergy: totalYearAdjustedMontlyEnergy,
		irradiance: (designIrradiance + getPercentage(designIrradiance, IRRADIANCE_UPRATE_RATE)) / totalNoOfPanelsSelectedByUser,
		facets,
		systemSize: (totalNoOfPanelsSelectedByUser * panelCapacityWatts),
		energy: deratedEnergy,
		offset,
		totalPanels: totalNoOfPanelsSelectedByUser,
		billAmount: currentBillAmount,
		panelKey: panel.key,
		panel,
		designUUID,
		annualUsage: annualInputEnergy,
		hourlyEnergy
	};

	console.log(` 
			design attributes with derate factor  ${energyDerateRate} totalPanelsSelected ${totalNoOfPanelsSelectedByUser}\n
			deratedYearlyEnergy ${design.yearlyEnergy} , normalYearlyEnergy ${yearlyEnergy} \n
			deratedMontylyEnergy ${design.monthlyEnergy} , normalMonthlyEnergy ${monthlyEnergy} \n
			average upratedIrradiance ${design.irradiance} , avergae normalIrradiance ${designIrradiance / totalNoOfPanelsSelectedByUser} \n
			uprate irradiance rate ${IRRADIANCE_UPRATE_RATE}
	`);

	return {
		selectedPanels,
		design
	};
});

function getHouseHourlyEngery(
	hourlyEngeryTOFAndSolarAccessData: HourlyEngeryTOFAndSolarAccessData | undefined,
	selectedPanels: string[], energyDerateRate: number, annualEnergy: number) {
	
	const allSegmentsHourlyEnergy = hourlyEngeryTOFAndSolarAccessData?.hourlyProductionData || [];

	if (!allSegmentsHourlyEnergy.length || !selectedPanels.length) {
		return [];
	}

	let houseHourlyEnergy = new Array(8760).fill(0);
	let totalYearhourlyEnergy = 0, panelCount = 0;

	for (let i = 0; i < allSegmentsHourlyEnergy.length; i++) {
		const hourlyEnergyPerSegment = allSegmentsHourlyEnergy[i];
		const hourlyEnergyPerPanel = hourlyEnergyPerSegment.hourlyProduction;

		for (const [pID, energyData] of Object.entries(hourlyEnergyPerPanel)) {
			if (selectedPanels.includes(pID)) {
				panelCount += 1;
				energyData.hourlyProduction.forEach((production: number, index: number) => {
					const deratedEnergy = (getDeratedValue(production, energyDerateRate));
					houseHourlyEnergy[index] += deratedEnergy;
					totalYearhourlyEnergy += deratedEnergy;
				});
			}

			if (panelCount === selectedPanels.length) {
				break;
			}
		}

		if (panelCount === selectedPanels.length) {
			break;
		}
	}

	console.log({
		'Hourly_Energy': houseHourlyEnergy,
		'Total_Hourly_Energy': totalYearhourlyEnergy,
	});

	const { adjustedEnergy } = adjustEnergyToMatchAnnual(annualEnergy, houseHourlyEnergy, totalYearhourlyEnergy);
	houseHourlyEnergy = adjustedEnergy;

	return houseHourlyEnergy;
}

function adjustEnergyToMatchAnnual(annualEnergy: number, energy: number[], totalEngery: number) {
	let adjustedEnergy: number[] = energy, adjustedTotal = 0;
	if (!totalEngery) return { adjustedEnergy, adjustedTotal };
	const ratio = annualEnergy / totalEngery;

	adjustedEnergy = energy.map((e: number) => {
		const adjustedValue = e * (ratio);
		adjustedTotal += adjustedValue;
		return adjustedValue;
	});

	return { adjustedEnergy, adjustedTotal };

}

function getTsrfTofAndSolarAccessDataPerFacet(
	tOFAndSolarAccessData: Record<string, TOFAndSolarAccessPerSgement>,
	selectedPanels: string[],
	roofId: string
) {
	const defaultTsrfTofAndSolarAccessData = {
		solarAccess: 0,
		solarAccessMonthly: new Array(12).fill(0),
		annualTOF: 0,
		annualTSRF: 0
	};
	if (!Object.keys(tOFAndSolarAccessData)?.length) {
		return defaultTsrfTofAndSolarAccessData;
	}

	const tofAndSolarAccessPerSegment = tOFAndSolarAccessData[roofId];
	if(!tofAndSolarAccessPerSegment) return defaultTsrfTofAndSolarAccessData;

	const { annualTOF, solarAccessByPanel } = tofAndSolarAccessPerSegment;
	let totalAnnualSolarAccess = 0, panelCount = 0;
	const totalMonthlySolarAccess = new Array(12).fill(0) as number[];
	Object.entries(solarAccessByPanel).forEach(solrAccess => {
		const [panelID, solarAccessPerPanel] = solrAccess;
		if (selectedPanels.includes(panelID)) {
			panelCount += 1;
			totalAnnualSolarAccess += solarAccessPerPanel.annualSolarAccess;
			solarAccessPerPanel.monthlySolarAccess.forEach((val, index) => totalMonthlySolarAccess[index] += val);
		}
	});

	if (!panelCount) {
		return defaultTsrfTofAndSolarAccessData;
	}

	const avgAnnualSolarAccess = totalAnnualSolarAccess / panelCount;
	const avgMonthlySolarAccess = totalMonthlySolarAccess.map((total) => total / panelCount);
	const annualTSRF = (annualTOF * avgAnnualSolarAccess) / 100;

	return {
		solarAccess: avgAnnualSolarAccess,
		solarAccessMonthly: avgMonthlySolarAccess,
		annualTOF,
		annualTSRF
	};
}

function reStructureTOFAndSolarAccessData(hourlyEngeryTOFAndSolarAccessData: HourlyEngeryTOFAndSolarAccessData | undefined) {
	const tOFAndSolarAccessObj: Record<string, TOFAndSolarAccessPerSgement> = {};
	if (!hourlyEngeryTOFAndSolarAccessData || !hourlyEngeryTOFAndSolarAccessData?.tofAndSolarAccess?.length) {
		return tOFAndSolarAccessObj;
	}
	const tofAndSolarAccessPerSegment = hourlyEngeryTOFAndSolarAccessData.tofAndSolarAccess;
	tofAndSolarAccessPerSegment.forEach(s => tOFAndSolarAccessObj[s.segmentId] = s);
	return tOFAndSolarAccessObj;
}

// makes unselected invlisble
export function changeVisibilityOfPanelsAndGetCanvasImage(konvaObj: Konva.Stage, visiblily: boolean, imageHeight?: number) {
	const panelObjs = (konvaObj.find('.' + KonvaGroupNames.existingPanelsGroup) as Konva.Group[]).map(g => g.children).flat();
	if (!panelObjs) return;
	for (let i = 0; i < panelObjs?.length; i++) {
		const line = panelObjs[i] as Konva.Line;
		if (line.attrs['fill'] === PANEL_OFF_COLOR)
			line.visible(visiblily);
	}
	
	const edgeLengthGroup = (konvaObj.findOne('.' + KonvaGroupNames.EDGE_LENGHT) as Konva.Group);
	edgeLengthGroup?.children?.forEach(eL => {
		eL.visible(visiblily);
	});

	return getCanvasImage(konvaObj, imageHeight);
}
export function getCanvasImage(konvaObj: Konva.Stage, imageHeight?: number){
	const pixelRatio = imageHeight ? Math.max(STANDARD_IMAGERY_SIZE / imageHeight, 0.8) : undefined;

	if(pixelRatio) return konvaObj.toDataURL({ pixelRatio });
	return konvaObj.toDataURL();
}

// get abosulte postions after translations
export function getAbsolutePoints(shape: Konva.Line, scale: Vector2d, stage: Konva.Stage) {
	// shape-local -> absolute canvas-pixel space (bakes in every ancestor transform, including the stage's own)
	const shapeTransform = shape.getAbsoluteTransform();
	// absolute canvas-pixel space -> stage-local (world) space, undoing the stage's own pan/zoom/rotation
	const stageInverseTransform = stage.getAbsoluteTransform().copy().invert();

	// Get the original points of the shape
	const points = shape.points();

	// Calculate the new absolute positions of the points
	const absolutePoints = [];
	for (let i = 0; i < points.length; i += 2) {
		const point = { x: points[i], y: points[i + 1] };
		const worldPoint = stageInverseTransform.point(shapeTransform.point(point));
		absolutePoints.push(worldPoint.x / scale.x, worldPoint.y / scale.y);
	}

	return absolutePoints;
}

export function transformPointsToOriginalScale(scale: Vector2d, point: number[]) {
	const {x, y} = scale;
	return [point[0] / x, point[1] / y];
}

export function getCanvasImageForThreeMode(
	konvaObj: Konva.Stage,
	fullViewEnabled: boolean,
	groundMountFacetIDs: string[],
	designType: DesignType,
) {
	const clonedStage = konvaObj.clone();
	// The 3D terrain's DSM geometry is always built in a fixed, north-up reference frame,
	// independent of the 2D view's rotation — but this clone bakes the terrain's TEXTURE from
	// whatever the 2D stage currently looks like. Leaving a non-zero rotation on the clone bakes
	// a rotated texture onto unrotated geometry, visibly misaligning the 3D model (and its north
	// face) from the actual roof shape. Reset it first, same as RoofImageBuilder's export clone.
	clonedStage.rotation(0);
	const scale = { x: DEFAULT_KONVA_SCALE, y: DEFAULT_KONVA_SCALE };
	if (fullViewEnabled) {
		// const raster= getPanelDrawerGroup(konvaObj)?.find('Image')[0] as Konva.Image;
		const raster = getBaseRasterImage(clonedStage);
		if (raster) {
			const layerScale = raster.getLayer()?.scaleX() || 1;
			scale.x = clonedStage.width() / (raster.width() * layerScale);
			scale.y = clonedStage.height() / (raster.height() * layerScale);
		}
		clonedStage.offset({ x: 0, y: 0 });
	}
	//Not setting state for this since these are temprory whe n 3D is enabled.
	clonedStage.position(DEFAULT_KONVA_STAGE_POSITION);
	clonedStage.scale(scale);

	// Hide tree circles so they don't appear flat on the 3D mesh
	const treeGroup = clonedStage.findOne('.tree-drawer');
	if (treeGroup) treeGroup.hide();

	const flattenArea = clonedStage.findOne('.flatten-area-drawer');
	if (flattenArea) flattenArea.hide();

	//Hide dsm edits:
	const dsmEditsGroup = clonedStage.findOne('.DSM_edits');
	if (dsmEditsGroup) dsmEditsGroup.hide();

	// Ground-mount/flat panels get their own 3D structure — hide here to avoid duplicating them.
	groundMountFacetIDs.forEach(id => {
		const panelsGroup = clonedStage.findOne(`#${getExistingPanelsGroupID(id)}`);
		if (panelsGroup) panelsGroup.hide();
	});

	// Irradiance's "Panels On" can hide the facet groups in 2D — always bake them, since the 3D
	// heatmap is cut out around panels (or covers them) and irradiance can now toggle inside 3D.
	clonedStage.find(`.${KonvaGroupNames.existingFacetsGroup}`).forEach(group => group.show());

	// Heatmap has its own 3D layer instead (ThreeDIrradianceLayer.tsx) — never bake it in.
	const irradianceGroup = clonedStage.findOne(`#${konvaImageIds.irradianceImageGroup}`);
	if (irradianceGroup) irradianceGroup.hide();

	const obstructionsGroup = clonedStage.findOne(`.${KonvaGroupNames.obstructionsGroup}`);
	if (obstructionsGroup) obstructionsGroup.hide();

	// Roof designs should keep showing the facet outline in 3D - only Solar hides it.
	if (designType === 'SOLAR') {
		const facetLines = clonedStage.find(`.${KonvaGroupNames.existingFacet}`);
		facetLines.forEach(facetLine => facetLine.hide());
	}

	const img = clonedStage.toDataURL({ pixelRatio: 1, mimeType: 'image/jpeg' });
	// console.log(img);
	clonedStage.destroy();
	return img;
}

export function adjustScalingWhileSavingDesignImage(konvaObj: Konva.Stage, imageHeight: number) {
	//Not setting state for this since these are temprory whe n saving canvas image
	// Always save north-up first, regardless of any in-progress rotate-mode view, so the
	// scale/position reset below (and the saved image) never come out distorted by a leftover rotation.
	konvaObj.rotation(0);
	const scale= setZoomlevelOnSave(konvaObj.scaleX(), imageHeight);
	konvaObj.scale({ x: scale, y: scale });
	konvaObj.position(DEFAULT_KONVA_STAGE_POSITION);
	if (scale > DEFAULT_KONVA_SCALE) {
		setZoomLevelOnStageFromCenter(konvaObj);
	} 
}

export function setZoomlevelOnSave(zoom: number, imageHeight: number) {
	const maxZoomLimit = MAX_ZOOM_LIMIT_FOR_SAVE * imageHeight / STANDARD_IMAGERY_SIZE;
	// const minZoomLimit = DEFAULT_KONVA_SCALE * imageHeight / STANDARD_IMAGERY_SIZE;
	if (zoom < DEFAULT_KONVA_SCALE) return DEFAULT_KONVA_SCALE;
	else if (zoom > maxZoomLimit) return maxZoomLimit ;
	return zoom;
}

export function getIsApprovalRequired(isSunroofAvailable: boolean, mode: DesignModes): boolean {
	return isSunroofAvailable && !isModeDefault(mode);
}

export function getFormattedDate(date: Date){
	return new Date(date).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	});
}

